import { ArticleResolution, FeatureRequestResolutions } from '@wix/answers-api';
import { Box, Tag, Text, Tooltip } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import Long from 'long';
import React, { FC, useCallback } from 'react';

import { isNumber } from '../../../../util/guards';
import { HelpCenterArticle } from '../../types';
import s from './styles.scss';

export interface ResolutionTagProps {
  article: HelpCenterArticle;
}

export const statusToClass: Record<FeatureRequestResolutions, string> = {
  [ArticleResolution.COLLECTING_VOTES]: s.collectingVotes,
  [ArticleResolution.PLANNED]: s.planned,
  [ArticleResolution.IN_DEVELOPMENT]: s.inDev,
  [ArticleResolution.COMING_SOON]: s.preLaunch,
  [ArticleResolution.PENDING_RELEASE]: s.pendingRelease,
  [ArticleResolution.RELEASED]: s.released,
};

export const ResolutionStatusTag: FC<ResolutionTagProps> = ({ article }) => {
  const { t } = useTranslation();
  const { resolution, resolutionEta } = article;

  const launchedTagText = useCallback(
    (eta: Long | number) => {
      const etaNumber = isNumber(eta) ? eta : new Long(eta.low, eta.high).toNumber();
      const date = new Date(etaNumber);
      const month = date.getUTCMonth();
      const monthText = t(`month_${month}`);
      const year = date.getUTCFullYear();
      return t('article_resolution_launched', {
        month: monthText,
        year,
      });
    },
    [t],
  );

  if (!resolution) {
    return null;
  }

  return (
    <Box>
      <Tooltip size="small" content={t(`article_resolution_tooltip_${article.resolution}`)}>
        <Tag
          size="small"
          className={`${s.statusTag} ${statusToClass[resolution]}`}
          id={`${article.resolution}`}
          theme="warning"
          removable={false}
        >
          <Text size="small">
            {resolution === ArticleResolution.RELEASED && resolutionEta
              ? launchedTagText(resolutionEta)
              : t(`article_resolution_${resolution}`)}
          </Text>
        </Tag>
      </Tooltip>
    </Box>
  );
};
